'use client';

import { trackEmailQuoteConfirmation } from '@workspace/utils';
import { useSearchParams } from 'react-router-dom';
import { useGlobalForm, useSmartFormGlobal } from '../contexts';
import { Button, Modal } from './';

interface ModalOptionRecontactConfig {
    title?: string;
    description?: string;
    buttonText?: string;
    downloadText?: string;
    downloadTextFC?: string;
    documentsTitle?: string;
    documentInfoText?: string;
    dispositionsGeneralesText?: string;
    conventionAssistanceText?: string;
    documentInformationAssistanceText?: string;
    lettreRGPDText?: string;
}

interface ModalOptionRecontactProps {
    config?: ModalOptionRecontactConfig;
}

const defaultConfig: ModalOptionRecontactConfig = {
    title: "Votre proposition d'assurance",
    description: "Votre proposition d'assurance a été envoyée sur l'adresse e-mail suivante :",
    buttonText: 'Je finalise la souscription',
    downloadText: "Télécharger la proposition d'assurance",
    downloadTextFC: "Télécharger la fiche d'Information et Conseil",
    documentsTitle: 'Retrouvez aussi en téléchargement :',
    documentInfoText: "Document d'information sur le produit Trottinettes et Plus",
    dispositionsGeneralesText: 'Dispositions Générales',
    conventionAssistanceText: "Convention d'Assistance",
    documentInformationAssistanceText: "Document d'information sur les garanties d'Assistance",
    lettreRGPDText: "Lettre RGPD",
};

export function ModalOptionRecontact({ config = defaultConfig }: ModalOptionRecontactProps) {
    const { actions, others } = useGlobalForm();
    const methods = useSmartFormGlobal();
    const [searchParams, setSearchParams] = useSearchParams();
    const apporteur = others?.apporteur?.codeBelair;

    const mergedConfig = { ...defaultConfig, ...config };

    const handleContinue = () => {
        trackEmailQuoteConfirmation(apporteur);
        closeModal();
        actions.goNextQuestion();
    };

    const closeModal = () => {
        setSearchParams();
    };

    const email = methods.watch('DemandeTarif.ListePersonnes.0.Email');
    const pdfDevisUrl = methods.watch('Devis.UrlPDFDevis');
    const pdfFicheConseilUrl = methods.watch('Devis.UrlPDFFicheConseil');
    const documentInfoUrl = methods.watch('Tarif.UrlPDFDocumentInformation');
    const dispositionsGeneralesUrl = methods.watch('Tarif.UrlPDFDispositionsGenerales');
    const conventionAssistanceUrl = methods.watch('Tarif.UrlPDFConventionAssistance');
    const documentInformationAssistanceUrl = methods.watch('Tarif.UrlPDFDocumentInformationAssistance');
    const documentRGPDUrl = methods.watch('Tarif.UrlPDFLettreRGPD');

    return (
        <Modal
            className="max-w-xl"
            onClose={closeModal}
            open={searchParams.get('modal') === 'RecevoirEmail'}
            title={mergedConfig.title}
        >
            <Modal.Body className="pt-4 pb-8 mt-4 border-t border-neutral-200 text-sm">
                {apporteur === 43397 && (
                    <p className="mb-4">
                        {mergedConfig.description} <span className="text-primary font-medium">{email}</span>
                    </p>
                )}

                <Button
                    type="button"
                    onClick={handleContinue}
                    className="w-full"
                    arrow={false}
                >
                    {mergedConfig.buttonText}
                </Button>

                {pdfDevisUrl && (
                    <a
                        className="text-center block mt-3 font-medium text-primary hover:text-primary-600 hover:underline"
                        href={pdfDevisUrl}
                        rel="noreferrer noopener"
                        target="_blank"
                    >
                        {mergedConfig.downloadText}
                    </a>
                )}
                {pdfFicheConseilUrl && (
                    <a
                        className="text-center block mt-3 font-medium text-primary hover:text-primary-600 hover:underline"
                        href={pdfFicheConseilUrl}
                        rel="noreferrer noopener"
                        target="_blank"
                    >
                        {mergedConfig.downloadTextFC}
                    </a>
                )}

                {(documentInfoUrl || dispositionsGeneralesUrl || conventionAssistanceUrl || documentInformationAssistanceUrl || documentRGPDUrl) && (
                    <>
                        <p className="mt-8">{mergedConfig.documentsTitle}</p>

                        <ul className="list-disc list-inside mt-2 space-y-1">
                            {documentInfoUrl && (
                                <li>
                                    <a
                                        className="text-primary font-medium hover:text-primary-600 hover:underline"
                                        href={documentInfoUrl}
                                        rel="noreferrer noopener"
                                        target="_blank"
                                    >
                                        {mergedConfig.documentInfoText}
                                    </a>
                                </li>
                            )}
                            {dispositionsGeneralesUrl && (
                                <li>
                                    <a
                                        className="text-primary font-medium hover:text-primary-600 hover:underline"
                                        href={dispositionsGeneralesUrl}
                                        rel="noreferrer noopener"
                                        target="_blank"
                                    >
                                        {mergedConfig.dispositionsGeneralesText}
                                    </a>
                                </li>
                            )}
                            {conventionAssistanceUrl && (
                                <li>
                                    <a
                                        className="text-primary font-medium hover:text-primary-600 hover:underline"
                                        href={conventionAssistanceUrl}
                                        rel="noreferrer noopener"
                                        target="_blank"
                                    >
                                        {mergedConfig.conventionAssistanceText}
                                    </a>
                                </li>
                            )}
                            {documentInformationAssistanceUrl && (
                                <li>
                                    <a
                                        className="text-primary font-medium hover:text-primary-600 hover:underline"
                                        href={documentInformationAssistanceUrl}
                                        rel="noreferrer noopener"
                                        target="_blank"
                                    >
                                        {mergedConfig.documentInformationAssistanceText}
                                    </a>
                                </li>
                            )}
                            {documentRGPDUrl && (
                                <li>
                                    <a
                                        className="text-primary font-medium hover:text-primary-600 hover:underline"
                                        href={documentRGPDUrl}
                                        rel="noreferrer noopener"
                                        target="_blank"
                                    >
                                        {mergedConfig.lettreRGPDText}
                                    </a>
                                </li>
                            )

                            }
                        </ul>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}
